import { memo } from "react"

import styled from "styled-components"

import { borderRadiusNumeric, IconTickSmall, IconMinusSmall, colors } from "@izettle/otto"

import { Icon } from "../icon"
import { SpinnerSimple } from "../spinner-simple"

import { selectorStates } from "./base"

const MARKER_SIZE = 16
const CONTAINER_SIZE = 24
const CORNER_RADIUS = borderRadiusNumeric.small
const FOCUS_PADDING = 6
const FOCUS_SIZE = CONTAINER_SIZE + FOCUS_PADDING * 2
const FOCUS_RADIUS = CORNER_RADIUS + FOCUS_PADDING

const CheckboxMarker = styled.div.attrs<{
  indeterminate?: boolean
  checked?: boolean
}>(({ indeterminate, checked }) => {
  if (checked) {
    return {
      children: <Icon svgIcon={IconTickSmall} />,
    }
  } else if (indeterminate) {
    return {
      children: <Icon svgIcon={IconMinusSmall} />,
    }
  }
})<{ indeterminate?: boolean; checked?: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  color: inherit;
  font-size: ${MARKER_SIZE}px;
  font-weight: 800;
  transform: translate(-50%, -50%);

  &:before {
    content: "";
    transition: transform 0.12s ease;
    transform: scale(0);
  }
`

const CheckboxContainer = styled.div`
  position: relative;

  flex-shrink: 0;
  height: ${CONTAINER_SIZE}px;
  width: ${CONTAINER_SIZE}px;
  border: 1px solid;
  /* stylelint-disable-next-line @izettle/border-radius */
  border-radius: ${CORNER_RADIUS}px;

  transition-property: background-color, border-color, color;
  transition-duration: 0.12s;
  transition-timing-function: ease;

  /* Default */
  background-color: ${colors.selector.base};
  border-color: ${colors.selector.initial.default};
  color: ${colors.selector.initial.default};

  ${selectorStates.hover} {
    background-color: ${colors.selector.base};
    border-color: ${colors.selector.initial.hover};
    color: ${colors.selector.initial.hover};
  }

  ${selectorStates.active} {
    background-color: ${colors.selector.base};
    border-color: ${colors.selector.initial.active};
    color: ${colors.selector.initial.active};
  }

  /* Selected */
  ${selectorStates.checked},
  ${selectorStates.indeterminate} {
    > ${CheckboxMarker}:before {
      transform: scale(1);
    }

    background-color: ${colors.selector.selected.default};
    border-color: ${colors.selector.selected.default};
    color: ${colors.selector.base};

    ${selectorStates.hover} {
      background-color: ${colors.selector.selected.hover};
      border-color: ${colors.selector.selected.hover};
      color: ${colors.selector.base};
    }

    ${selectorStates.active} {
      background-color: ${colors.selector.selected.active};
      border-color: ${colors.selector.selected.active};
      color: ${colors.selector.base};
    }
  }

  /* Invalid */
  ${selectorStates.invalid} {
    background-color: ${colors.selector.base};
    border-color: ${colors.selector.error.default};
    color: ${colors.selector.base};

    ${selectorStates.hover} {
      background-color: ${colors.selector.base};
      border-color: ${colors.selector.error.hover};
      color: ${colors.selector.base};
    }

    ${selectorStates.active} {
      background-color: ${colors.selector.base};
      border-color: ${colors.selector.error.active};
      color: ${colors.selector.base};
    }
  }

  /* Invalid & Selected */
  ${selectorStates.invalidChecked},
  ${selectorStates.invalidIndeterminate} {
    background-color: ${colors.selector.error.default};
    border-color: ${colors.selector.error.default};
    color: ${colors.selector.base};

    ${selectorStates.hover} {
      background-color: ${colors.selector.error.hover};
      border-color: ${colors.selector.error.hover};
      color: ${colors.selector.base};
    }

    ${selectorStates.active} {
      background-color: ${colors.selector.error.active};
      border-color: ${colors.selector.error.active};
      color: ${colors.selector.base};
    }
  }

  /* Disabled */
  ${selectorStates.disabled} {
    background-color: ${colors.selector.disabled.base} !important;
    border-color: ${colors.selector.disabled.accent} !important;
    color: ${colors.selector.disabled.accent} !important;
  }

  ${selectorStates.disabledChecked},
  ${selectorStates.disabledIndeterminate} {
    background-color: ${colors.selector.disabledSelected.base} !important;
    border-color: ${colors.selector.disabledSelected.base} !important;
    color: ${colors.selector.disabledSelected.accent} !important;
  }

  /* Focus */
  &:before {
    pointer-events: none;
    content: "";
    position: relative;
    display: block;
    box-sizing: border-box;
    border: 1px solid;
    height: ${FOCUS_SIZE}px;
    width: ${FOCUS_SIZE}px;
    /* stylelint-disable-next-line @izettle/border-radius */
    border-radius: ${FOCUS_RADIUS}px;
    z-index: -1;
    top: -${FOCUS_PADDING + 1}px;
    left: -${FOCUS_PADDING + 1}px;

    border-color: ${colors.selector.focus.border};
    background-color: ${colors.selector.focus.background};

    transition: opacity 0.12s ease;
    opacity: 0;
  }

  ${selectorStates.focus}:before {
    opacity: 1;
  }
`

const StyledSpinner = styled(SpinnerSimple)`
  position: absolute;
  top: calc((${CONTAINER_SIZE - MARKER_SIZE}px - 2px) / 2);
  left: calc((${CONTAINER_SIZE - MARKER_SIZE}px - 2px) / 2);
  color: inherit;
  height: ${MARKER_SIZE}px;
  width: ${MARKER_SIZE}px;
  border-color: currentColor;
`

export const CheckboxStyles = memo<{
  loading?: boolean
  indeterminate?: boolean
  checked?: boolean
}>(({ loading, indeterminate, checked }) => (
  <CheckboxContainer>
    {loading ? (
      <StyledSpinner />
    ) : (
      <CheckboxMarker indeterminate={indeterminate} checked={checked} />
    )}
  </CheckboxContainer>
))
